import {
	FILTER_JOB_KEY,
	FILTER_LOCATION_KEY,
	FILTER_SKILL_KEY,
	FILTER_WORK_CENTERS_KEY,
} from "../../../assets/constants";
import { AppliedFilter, DataRelation } from "../../filters/filter-context";

// Helpers shared by the insight/summary reports (Cross Skilling Summary,
// Skill Summary Report tabs) to translate the standard AppliedFilters chips
// into the id sets their existing filtering logic consumes. All return null
// when the filter is not applied (null = no constraint).

export const getAppliedLocationIds = (
	appliedFilters: AppliedFilter[]
): number[] | null => {
	const chip = appliedFilters.find((f) => f.key === FILTER_LOCATION_KEY);
	return chip && chip.data.length > 0
		? chip.data.map((item) => Number(item.id))
		: null;
};

export const getAppliedWorkCenterIds = (
	appliedFilters: AppliedFilter[]
): Set<number> | null => {
	const chip = appliedFilters.find((f) => f.key === FILTER_WORK_CENTERS_KEY);
	return chip && chip.data.length > 0
		? new Set(chip.data.map((item) => Number(item.id)))
		: null;
};

export interface SkillWorkCenterConstraint {
	allowedSkillIds: Set<number>;
	skillsWithJobs: Set<number>;
}

// Skills have no direct work-center field, so Work Center chips are resolved
// to skills through the jobs mapping (workCenters→jobs and jobs→skills
// relations from GetFiltersV2): a skill passes when any of its jobs belongs
// to a selected work center. Skills with no job association ignore the Work
// Center filter (location check only). Fails open (null = no constraint)
// when the relations are missing.
export const buildSkillWorkCenterConstraint = (
	appliedWorkCenterIds: Set<number> | null,
	dataRelations: DataRelation[] | undefined
): SkillWorkCenterConstraint | null => {
	if (!appliedWorkCenterIds) return null;
	const relations = dataRelations ?? [];
	const wcToJobs = relations.find(
		(r) => r.from === FILTER_WORK_CENTERS_KEY && r.to === FILTER_JOB_KEY
	);
	const jobsToSkills = relations.find(
		(r) => r.from === FILTER_JOB_KEY && r.to === FILTER_SKILL_KEY
	);
	if (!wcToJobs?.mappings || !jobsToSkills?.mappings) return null;

	const selectedJobIds = new Set<number>();
	wcToJobs.mappings.forEach((mapping) => {
		if (appliedWorkCenterIds.has(mapping.fromId)) {
			(mapping.toIds || []).forEach((id) => selectedJobIds.add(id));
		}
	});

	const allowedSkillIds = new Set<number>();
	const skillsWithJobs = new Set<number>();
	jobsToSkills.mappings.forEach((mapping) => {
		(mapping.toIds || []).forEach((id) => {
			skillsWithJobs.add(id);
			if (selectedJobIds.has(mapping.fromId)) {
				allowedSkillIds.add(id);
			}
		});
	});

	return { allowedSkillIds, skillsWithJobs };
};

// True when the skill passes the Work Center constraint (or no constraint).
export const skillMatchesWorkCenters = (
	skillId: number | string,
	constraint: SkillWorkCenterConstraint | null
): boolean => {
	if (!constraint) return true;
	const id = Number(skillId);
	if (!constraint.skillsWithJobs.has(id)) return true;
	return constraint.allowedSkillIds.has(id);
};
